<template>
  <v-menu
    v-model="menuOpen"
    offset-y
    left
    :close-on-content-click="false"
    content-class="trading-calculator-menu"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-tooltip bottom content-class="tooltip-dark">
        <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
          <v-btn
            fab
            small
            elevation="0"
            :ripple="false"
            :color="$vuetify.theme.dark ? '#17140f' : '#F8F6FC'"
            v-bind="{ ...attrs, ...tooltipAttrs }"
            v-on="{ ...on, ...tooltipOn }"
          >
            <v-icon class="calculator-accent-icon">mdi-calculator-variant-outline</v-icon>
          </v-btn>
        </template>
        <span class="tooltip-text">Trading Calculators</span>
      </v-tooltip>
    </template>

    <v-card class="calculator-card" :class="{ 'calculator-card--dark': $vuetify.theme.dark }">
      <header class="calculator-header">
        <div>
          <v-icon small class="calculator-accent-icon">mdi-calculator-variant-outline</v-icon>
          <strong>Trading Calculators</strong>
        </div>
        <v-btn icon x-small @click="menuOpen = false">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </header>

      <div class="calculator-tabs">
        <button
          v-for="calculator in calculators"
          :key="calculator.value"
          :class="{ active: activeCalculator === calculator.value }"
          @click="activeCalculator = calculator.value"
        >
          <v-icon small>{{ calculator.icon }}</v-icon>
          {{ calculator.label }}
        </button>
      </div>

      <div class="calculator-body">
        <template v-if="activeCalculator === 'pip'">
          <div class="calculator-grid">
            <label>Trade Size (units)<input v-model.number="pip.units" type="number" min="0"></label>
            <label>Pip Size<input v-model.number="pip.pipSize" type="number" min="0" step="0.00001"></label>
            <label>Quote-to-Account Rate<input v-model.number="pip.rate" type="number" min="0" step="0.00001"></label>
          </div>
          <p class="calculator-formula">Trade Size × Pip Size ÷ Conversion Rate</p>
          <div class="calculator-result"><span>Pip Value</span><strong>{{ formatMoney(pipValue) }}</strong></div>
        </template>

        <template v-else-if="activeCalculator === 'margin'">
          <div class="calculator-grid">
            <label>Lots<input v-model.number="margin.lots" type="number" min="0" step="0.01"></label>
            <label>Contract Size<input v-model.number="margin.contractSize" type="number" min="0"></label>
            <label>Market Price<input v-model.number="margin.price" type="number" min="0" step="0.00001"></label>
            <label>Leverage (1:X)<input v-model.number="margin.leverage" type="number" min="1"></label>
          </div>
          <p class="calculator-formula">Lots × Contract Size × Market Price ÷ Leverage</p>
          <div class="calculator-result"><span>Required Margin</span><strong>{{ formatMoney(requiredMargin) }}</strong></div>
        </template>

        <template v-else-if="activeCalculator === 'swap'">
          <div class="calculator-grid">
            <label>Lots<input v-model.number="swap.lots" type="number" min="0" step="0.01"></label>
            <label>Contract Size<input v-model.number="swap.contractSize" type="number" min="0"></label>
            <label>Swap Points<input v-model.number="swap.points" type="number" step="0.01"></label>
            <label>Point Size<input v-model.number="swap.pointSize" type="number" min="0" step="0.00001"></label>
            <label>Nights<input v-model.number="swap.nights" type="number" min="0"></label>
          </div>
          <p class="calculator-formula">Lots × Contract Size × Swap Points × Point Size × Nights</p>
          <div class="calculator-result"><span>Swap Amount</span><strong :class="{ negative: swapAmount < 0 }">{{ formatMoney(swapAmount) }}</strong></div>
        </template>

        <template v-else>
          <div class="calculator-direction">
            <button :class="{ active: profit.direction === 'buy' }" @click="profit.direction = 'buy'">Buy</button>
            <button :class="{ active: profit.direction === 'sell' }" @click="profit.direction = 'sell'">Sell</button>
          </div>
          <div class="calculator-grid">
            <label>Lots<input v-model.number="profit.lots" type="number" min="0" step="0.01"></label>
            <label>Contract Size<input v-model.number="profit.contractSize" type="number" min="0"></label>
            <label>Open Price<input v-model.number="profit.openPrice" type="number" min="0" step="0.00001"></label>
            <label>Close Price<input v-model.number="profit.closePrice" type="number" min="0" step="0.00001"></label>
          </div>
          <p class="calculator-formula">(Close − Open) × Direction × Lots × Contract Size</p>
          <div class="calculator-result"><span>Estimated Profit</span><strong :class="{ negative: profitAmount < 0 }">{{ formatMoney(profitAmount) }}</strong></div>
        </template>
      </div>
    </v-card>
  </v-menu>
</template>

<script>
export default {
  name: "TradingCalculatorMenu",
  data() {
    return {
      menuOpen: false,
      activeCalculator: "pip",
      calculators: [
        { label: "Pip", value: "pip", icon: "mdi-chart-line-variant" },
        { label: "Margin", value: "margin", icon: "mdi-scale-balance" },
        { label: "Swap", value: "swap", icon: "mdi-swap-horizontal" },
        { label: "Profit", value: "profit", icon: "mdi-cash-plus" },
      ],
      pip: { units: 100000, pipSize: 0.0001, rate: 1 },
      margin: { lots: 1, contractSize: 100000, price: 1, leverage: 100 },
      swap: { lots: 1, contractSize: 100000, points: -1, pointSize: 0.00001, nights: 1 },
      profit: { direction: "buy", lots: 1, contractSize: 100000, openPrice: 1, closePrice: 1 },
    };
  },
  computed: {
    pipValue() {
      return this.safeDivide(this.pip.units * this.pip.pipSize, this.pip.rate);
    },
    requiredMargin() {
      return this.safeDivide(
        this.margin.lots * this.margin.contractSize * this.margin.price,
        this.margin.leverage
      );
    },
    swapAmount() {
      return (
        this.number(this.swap.lots) *
        this.number(this.swap.contractSize) *
        this.number(this.swap.points) *
        this.number(this.swap.pointSize) *
        this.number(this.swap.nights)
      );
    },
    profitAmount() {
      const direction = this.profit.direction === "sell" ? -1 : 1;
      return (
        (this.number(this.profit.closePrice) - this.number(this.profit.openPrice)) *
        direction *
        this.number(this.profit.lots) *
        this.number(this.profit.contractSize)
      );
    },
  },
  methods: {
    number(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : 0;
    },
    safeDivide(value, divisor) {
      const denominator = this.number(divisor);
      return denominator > 0 ? this.number(value) / denominator : 0;
    },
    formatMoney(value) {
      return this.number(value).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.calculator-card {
  width: 430px;
  overflow: hidden;
  border: 1px solid #d6e1eb;
  border-radius: 8px;
  background: #ffffff;
  color: #142033;
}
.calculator-card--dark {
  border-color: #2c2415;
  background: #121110;
  color: #f5efe4;
}
.calculator-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 46px;
  padding: 0 12px;
  border-bottom: 1px solid rgba(112, 135, 158, .22);
}
.calculator-header > div { display: flex; align-items: center; gap: 8px; }
.calculator-header strong { color: inherit; font-size: 13px; }
.calculator-tabs {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 5px;
  padding: 8px;
  border-bottom: 1px solid rgba(112, 135, 158, .18);
}
.calculator-tabs button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
  min-height: 48px;
  border: 1px solid rgba(112, 135, 158, .24);
  border-radius: 5px;
  color: #6c8094;
  font-size: 10px;
}
.calculator-tabs button.active {
  border-color: #e3ae3f;
  background: rgba(227, 174, 63, .14);
  color: #e3ae3f;
}
.calculator-tabs button.active .v-icon { color: #e3ae3f !important; }
.calculator-body { padding: 12px; }
.calculator-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 9px;
}
.calculator-grid label {
  color: #789;
  font-size: 9px;
}
.calculator-grid input {
  width: 100%;
  height: 34px;
  margin-top: 4px;
  padding: 0 9px;
  border: 1px solid rgba(112, 135, 158, .35);
  border-radius: 4px;
  background: rgba(128, 149, 170, .06);
  color: inherit;
  font-size: 11px;
  outline: none;
}
.calculator-grid input:focus { border-color: #e3ae3f; }
.calculator-formula {
  margin: 10px 0 8px;
  color: #789;
  font-size: 9px;
}
.calculator-result {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 48px;
  padding: 0 12px;
  border: 1px solid rgba(227, 174, 63, .32);
  border-radius: 5px;
  background: rgba(227, 174, 63, .1);
}
.calculator-result span { color: #789; font-size: 10px; }
.calculator-result strong { color: #e3ae3f; font-size: 17px; }
.calculator-result strong.negative { color: #ff5b5b; }
.calculator-direction { display: flex; gap: 6px; margin-bottom: 9px; }
.calculator-direction button {
  flex: 1;
  height: 30px;
  border: 1px solid rgba(112, 135, 158, .3);
  border-radius: 4px;
  color: #789;
  font-size: 10px;
}
.calculator-direction button.active {
  border-color: #e3ae3f;
  background: rgba(227, 174, 63, .14);
  color: #e3ae3f;
}
.calculator-accent-icon { color: #fed831 !important; }
@media (max-width: 500px) {
  .calculator-card { width: min(94vw, 430px); }
}
</style>
